import { useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MapPin, Users, Bed, Calendar, Building2, BadgeCheck } from "lucide-react";
import FavoritosButton from "@/components/FavoritosButton";
import { useAuthState } from "@/hooks/useAuthState";
import { getCoverImageUrl } from "@/lib/imageMap";
import { getUserImageUrl } from "@/config/api";
import { SearchService, SearchResult, SearchFilters } from "@/services/searchService";
import { UserService } from "@/services/userService";
import TopHeroTemplate from "@/components/layout/TopHeroTemplate";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";

const PAGE_SIZE = 8;
const HERO_IMAGES = [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5];

// Recebe uma string no formato "yyyy-MM-dd" e retorna "dd/MM/yyyy"
const formatDateWithoutTimezone = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const getDevelopmentRoute = (developmentName: string | null | undefined): string | null => {
  if (!developmentName) return null;
  
  const nameLower = developmentName.toLowerCase().trim();
  
  if (nameLower.includes('porto 2 life') || nameLower.includes('porto2life') || nameLower.includes('porto-2-life')) {
    return '/empreendimentos/porto-2-life';
  }
  if (nameLower.includes('exclusive gramado') || nameLower.includes('exclusive-gramado')) {
    return '/empreendimentos/exclusive-gramado';
  }
  
  return null;
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const empreendimento = searchParams.get("empreendimento") || "";
  const hospedes = searchParams.get("hospedes") || "";
  const dataInicio = searchParams.get("inicio") || "";
  const dataFim = searchParams.get("fim") || "";
  const ownerIdParam = searchParams.get("ownerId") || "";
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | null>(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const randomHeroImage = useMemo(() => {
    return HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)];
  }, []);

  useEffect(() => {
    const loadOwnerName = async () => {
      if (isAuthenticated && ownerIdParam) {
        try {
          const owner = await UserService.getUserById(parseInt(ownerIdParam));
          if (owner) {
            setOwnerName(owner.name);
          } else {
            setOwnerName(null);
          }
        } catch (e) {
          console.error('Erro ao carregar nome do proprietário:', e);
          setOwnerName(null);
        }
      } else {
        setOwnerName(null);
      }
    };

    loadOwnerName();
  }, [ownerIdParam, isAuthenticated]);

  const buildFilters = useCallback((): SearchFilters => {
    const tipoServico = searchParams.get("tipoServico");
    const buscarDatasExatasParam = searchParams.get("buscarDatasExatas");
    return {
      empreendimento: empreendimento && empreendimento.trim() !== '' ? empreendimento.trim() : undefined,
      hospedes: hospedes ? parseInt(hospedes) : undefined,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      tipoServico: tipoServico && tipoServico.trim() !== '' ? tipoServico.trim() : undefined,
      ownerId: isAuthenticated && ownerIdParam ? parseInt(ownerIdParam) : undefined,
      buscarDatasExatas: buscarDatasExatasParam === "true",
    };
  }, [empreendimento, hospedes, dataInicio, dataFim, ownerIdParam, isAuthenticated, searchParams]);

  // Primeira página + reset ao mudar filtros
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        setSearchResults([]);
        setNextCursor(null);
        setHasMore(false);
        setTotalCount(null);

        const filters = buildFilters();
        const page = await SearchService.searchPropertiesPage({
          ...filters,
          limit: PAGE_SIZE,
          cursor: null,
          includeTotal: true,
        });

        if (cancelled) return;

        setSearchResults(page.items);
        setNextCursor(page.nextCursor);
        setHasMore(page.hasMore);
        if (typeof page.total === 'number') {
          setTotalCount(page.total);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Erro na busca:', e);
          setError('Erro ao realizar a busca. Tente novamente.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [empreendimento, hospedes, dataInicio, dataFim, ownerIdParam, isAuthenticated, searchParams, buildFilters]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;

    try {
      setLoadingMore(true);
      const filters = buildFilters();
      const cursor =
        nextCursor ??
        (searchResults.length
          ? searchResults[searchResults.length - 1].property.id
          : null);
      if (cursor == null) {
        setLoadingMore(false);
        return;
      }

      const page = await SearchService.searchPropertiesPage({
        ...filters,
        limit: PAGE_SIZE,
        cursor,
        includeTotal: false,
      });

      setSearchResults((prev) => {
        const seen = new Set(prev.map((r) => r.property.id));
        const merged = [...prev];
        for (const row of page.items) {
          if (!seen.has(row.property.id)) {
            seen.add(row.property.id);
            merged.push(row);
          }
        }
        return merged;
      });
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      console.error('Erro ao carregar mais:', e);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextCursor, loading, buildFilters, searchResults]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore || loading) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  const coverForList = (property: SearchResult['property']) =>
    getCoverImageUrl(property, true);

  const headingCount =
    totalCount != null ? totalCount : searchResults.length;

  return (
    <div className="min-h-screen bg-background">
      <TopHeroTemplate
        backgroundImage={randomHeroImage}
        navbarProps={{
          isAuthenticated,
          isOwner: userType === "owner",
          userName,
          userPhoto,
          userId,
          userType,
          cleanHeader: true,
          rightActionsCard: true,
        }}
      >
          <div className="relative">
            <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg leading-tight">
              Viva sua melhor experiência
            </h1>
            <p className="text-sm sm:text-xl md:text-2xl text-white/95 mb-6 drop-shadow-md leading-relaxed">
              Descubra o paraíso de Muro Alto no Porto 2 Life Resort em Porto de Galinhas
            </p>
          </div>

          <div className="mx-auto mt-4 sm:mt-6 w-full max-w-none">
            <SearchBar compactHeight />
          </div>
      </TopHeroTemplate>

      <section className="py-12 container mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {!loading && headingCount === 0
              ? 'Nenhum imóvel encontrado'
              : `${loading ? '…' : headingCount} imóveis encontrados`}
            {empreendimento && ` no ${empreendimento}`}
            {isAuthenticated && ownerIdParam && ownerName && ` de ${ownerName}`}
          </h2>
          
          {(dataInicio || dataFim) && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Período: {dataInicio && formatDateWithoutTimezone(dataInicio)} 
                {dataInicio && dataFim && ' até '} 
                {dataFim && formatDateWithoutTimezone(dataFim)}
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando propriedades...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive text-lg mb-2">Erro na busca</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">Nenhuma propriedade encontrada</p>
            <p className="text-muted-foreground">Tente ajustar os filtros de busca</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {searchResults.map((result) => {
                const property = result.property;
                const coverSrc = coverForList(property);
                return (
              <Card
                key={property.id}
                className="group cursor-pointer overflow-hidden border shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1"
                onClick={() => {
                  window.open(`/imovel/${property.id}`, '_blank', 'noopener,noreferrer');
                }}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={coverSrc}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.src.includes('/thumb/')) {
                        img.src = getCoverImageUrl(property, false);
                      }
                    }}
                  />
                  {Number(property.is_verified) === 1 && (
                    <div className="absolute top-3 left-3 bg-blue-500 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold shadow-lg z-10">
                      <BadgeCheck className="h-3 w-3" />
                      Verificado
                    </div>
                  )}
                  {isAuthenticated && (
                    <FavoritosButton
                      propertyId={property.id}
                      propertyTitle={property.title}
                      propertyImage={coverSrc}
                      propertyPrice={undefined}
                      propertyLocation={property.city}
                    />
                  )}
                  {isAuthenticated && (
                    <Avatar className="absolute bottom-3 left-3 h-9 w-9 border-2 border-background shadow-lg">
                      <AvatarImage 
                        src={property.owner_photo ? getUserImageUrl(property.owner_id, property.owner_photo) : undefined} 
                        alt={property.owner_name || "Proprietário"} 
                      />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {property.owner_name ? property.owner_name.charAt(0).toUpperCase() : "P"}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>

                <CardContent className="p-3 md:p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-base md:text-lg line-clamp-2 flex-1 leading-snug">
                      {property.title}
                    </h3>
                  </div>

                  {property.development_name && (() => {
                    const developmentRoute = getDevelopmentRoute(property.development_name);
                    return developmentRoute ? (
                      <div 
                        className="flex items-center text-primary font-medium text-sm mb-2 cursor-pointer hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(developmentRoute);
                        }}
                      >
                        <Building2 className="h-4 w-4 mr-1 shrink-0" />
                        <span className="line-clamp-1">{property.development_name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-primary font-medium text-sm mb-2">
                        <Building2 className="h-4 w-4 mr-1 shrink-0" />
                        <span className="line-clamp-1">{property.development_name}</span>
                      </div>
                    );
                  })()}

                  <div className="flex items-center text-muted-foreground text-sm mb-2">
                    <MapPin className="h-4 w-4 mr-1 shrink-0" />
                    <span className="line-clamp-1">{property.city} - {property.state}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-muted-foreground mb-3">
                    <div className="flex items-center gap-1">
                      <Bed className="h-4 w-4 shrink-0" />
                      {property.bedrooms} quartos
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 shrink-0" />
                      {property.max_guests} hóspedes
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 shrink-0" />
                      {result.availablePeriods.length} período{result.availablePeriods.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t pt-2 md:pt-3">
                    <div>
                      {result.minPrice === result.maxPrice ? (
                        <div>
                          <span className="text-xl md:text-2xl font-bold text-primary">
                            R$ {result.minPrice.toLocaleString('pt-BR')}
                          </span>
                          <span className="text-muted-foreground text-xs md:text-sm ml-1">
                            /noite
                          </span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-xl md:text-2xl font-bold text-primary">
                            R$ {result.minPrice.toLocaleString('pt-BR')}
                          </span>
                          <span className="text-muted-foreground text-xs md:text-sm ml-1">
                            - R$ {result.maxPrice.toLocaleString('pt-BR')} /noite
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
                );
              })}
            </div>

            <div ref={loadMoreRef} className="h-12 w-full flex items-center justify-center mt-8" aria-hidden />

            {loadingMore && (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary border-t-transparent" />
              </div>
            )}

            {!hasMore && searchResults.length > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                Fim dos resultados
              </p>
            )}
          </>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default SearchResults;
